import { mqttService } from '../../services/mqttService';
import {
    AmplifierData,
    AmplifierControlData,
    AmplifierProjectData,
    ProgramStatusMessage,
    HamlibControlRequestMessage,
    UpdateAlarmLogRequestMessage,
    UpdateAlarmLogStatusMessage,
    DeviceUpdateCommandMessage,
    RemoteControlMessage,
    RemoteControlData,
    ProgramStatusData,
    HamlibControlData,
    RemotePopupAction,
    RemotePopupMessage,
    RemotePopupState,
    RemotePopupType,
    RemoteRestartMessage,
    EngParamsMessage,
    EngParamsBackupAction,
    EngParamsBackupData,
    EngParamsBackupMessage,
} from './amplifier';
import { defaultRemotePopupType } from './remotePopupTypes';
import {
    isRemoteControlError,
    mapEngParamsToProjectData,
    mapRemoteControlToAmplifierData,
    mapRemoteControlToHamlibControl,
    mapRemoteControlToProgramStatus,
    mergeProgramStatusMessage,
} from './remoteControlMapper';
import {
    createRemoteControlMessageId,
    mapAmplifierControlDelta,
    mapAmplifierProjectDelta,
    mapAntennaConfigToDelta,
    mapHamlibControlDelta,
    mapProgramStatusDelta,
} from './remoteControlUplinkMapper';
import type { AntennaConfig } from './antennaConfig';

type AmplifierStatusSubscriber = {
    callback: (data: AmplifierData) => void;
    programStatusCallback?: (data: ProgramStatusMessage) => void;
    hamlibControlCallback?: (data: HamlibControlRequestMessage) => void;
    auditLogCallback?: (data: UpdateAlarmLogStatusMessage) => void;
    projectDataCallback?: (data: AmplifierProjectData) => void;
    remotePopupCallback?: (state: RemotePopupState) => void;
    remoteControlDeviceId?: string;
};

type PendingByDevice = {
    pendingAmplifierData: AmplifierData | null;
    pendingProgramStatus: ProgramStatusMessage | null;
    pendingHamlib: HamlibControlRequestMessage | null;
    pendingAuditLog: UpdateAlarmLogStatusMessage | null;
    pendingProjectData: AmplifierProjectData | null;
    throttleTimerId: ReturnType<typeof setTimeout> | null;
};



/**
 * AmplifierControl MQTT服务
 * 专门用于功放控制组件的MQTT通信
 */
class AmplifierMqttService {
    private paramCommandIntervalId: NodeJS.Timeout | null = null;

    private isDevMqttOptional(): boolean {
        return process.env.NODE_ENV === 'development';
    }

    /**
     * 失败时仍会抛出，便于上层提示用户。
     */
    private async ensureMqttReady(label: string): Promise<void> {
        if (!mqttService.isConnected()) {
            try {
                await mqttService.connect();
            } catch (err) {
                console.error(`AmplifierMqttService: MQTT连接失败，无法发送${label}:`, err);
                throw new Error('MQTT服务未连接');
            }
        }
        if (!mqttService.isServiceInitialized()) {
            console.error(`AmplifierMqttService: MQTT服务未初始化，无法发送${label}`);
            throw new Error('MQTT服务未初始化');
        }
    }

    /**
     * 启动参数命令定时任务（已禁用）
     * 前端不再定时发送 synCommand，仅保留接口以兼容现有调用；如有需要由后端或其他方式下发
     */
    startParamCommandInterval(_deviceId: string, _userId: number): void {
        this.stopParamCommandInterval();
    }
    /**
     * 发送 remote_control delta 到 json_command（仅包含变更字段）
     */
    async sendRemoteControlDelta(
        deviceId: string,
        data: Partial<RemoteControlData>,
        msgId?: string,
    ): Promise<void> {
        if (!data || Object.keys(data).length === 0) return;
        try {
            const message: RemoteControlMessage = {
                msg_id: msgId ?? createRemoteControlMessageId(),
                cmd: 'remote_control',
                timestamp: Math.floor(Date.now() / 1000),
                data,
            };
            await this.publishJsonCommand(deviceId, message as unknown as Record<string, unknown>);
        } catch (error) {
            console.error('AmplifierMqttService: remote_control delta 发送失败:', error);
            throw error;
        }
    }

    /** 清除当前活动报警（协议 §5.6.7 clear_alarm=true） */
    async sendClearAlarm(deviceId: string, msgId?: string): Promise<void> {
        await this.sendRemoteControlDelta(deviceId, { clear_alarm: true }, msgId);
    }

    /** 远程确认弹窗用户操作（协议 §5.7，下行须含 popup_id / popup_type / action） */
    async sendRemotePopupAction(
        deviceId: string,
        action: RemotePopupAction,
        context: { popupId: string; popupType: RemotePopupType },
        msgId?: string,
    ): Promise<void> {
        if (!context.popupId?.trim()) {
            throw new Error('remote_popup action 缺少 popup_id');
        }
        try {
            const message = {
                msg_id: msgId ?? createRemoteControlMessageId(),
                cmd: 'remote_popup',
                timestamp: Math.floor(Date.now() / 1000),
                data: {
                    popup_id: context.popupId,
                    popup_type: context.popupType,
                    action,
                },
            };
            await this.publishJsonCommand(deviceId, message);
        } catch (error) {
            console.error('AmplifierMqttService: remote_popup action 发送失败:', error);
            throw error;
        }
    }

    /** 远程重启（协议 §5.8 remote_restart） */
    async sendRemoteRestart(
        deviceId: string,
        options: { software?: boolean; mcu?: boolean },
        msgId?: string,
    ): Promise<void> {
        if (!options.software && !options.mcu) {
            throw new Error('remote_restart 至少需要 software 或 mcu 之一为 true');
        }
        try {
            const data: RemoteRestartMessage['data'] = {};
            if (options.software) data.software = true;
            if (options.mcu) data.mcu = true;
            const message: RemoteRestartMessage = {
                msg_id: msgId ?? createRemoteControlMessageId(),
                cmd: 'remote_restart',
                timestamp: Math.floor(Date.now() / 1000),
                data,
            };
            await this.publishJsonCommand(deviceId, message as unknown as Record<string, unknown>);
        } catch (error) {
            console.error('AmplifierMqttService: remote_restart 发送失败:', error);
            throw error;
        }
    }

    private async publishJsonCommand(deviceId: string, payload: Record<string, unknown>): Promise<void> {
        await this.ensureMqttReady('json_command');
        const mqttManager = mqttService.getMqttManager();
        const topic = `server/device/${deviceId}/json_command`;
        const { type: _legacyType, ...cleanPayload } = payload;
        await mqttManager.publishJson(topic, cleanPayload, 1, false);
        console.log('AmplifierMqttService: json_command指令发送成功:', cleanPayload);
    }

    /**
     * 发送 json_command（保留 update / update_audit_log / update_alarm_log / update_mcu_log 等非 remote_control cmd）
     */
    async sendJsonCommand(
        deviceId: string,
        message: UpdateAlarmLogRequestMessage | DeviceUpdateCommandMessage,
    ): Promise<void> {
        try {
            await this.publishJsonCommand(deviceId, message as unknown as Record<string, unknown>);
        } catch (error) {
            console.error('AmplifierMqttService: json_command指令发送失败:', error);
            throw error;
        }
    }

    /** 过渡期：发送非 remote_control 的旧格式 json_command（如导出等暂无 delta 字段的操作） */
    async sendLegacyJsonCommand(deviceId: string, payload: Record<string, unknown>): Promise<void> {
        try {
            await this.publishJsonCommand(deviceId, payload);
        } catch (error) {
            console.error('AmplifierMqttService: legacy json_command 发送失败:', error);
            throw error;
        }
    }

    async sendAmplifierControlDelta(
        deviceId: string,
        partial: Partial<AmplifierControlData>,
    ): Promise<void> {
        const delta = mapAmplifierControlDelta(partial);
        await this.sendRemoteControlDelta(deviceId, delta);
    }

    /** 协议 §5.6.3：天线配置（三组平行数组须完整 6 项） */
    async sendAntennaConfigDelta(deviceId: string, config: AntennaConfig): Promise<void> {
        const delta = mapAntennaConfigToDelta(config);
        await this.sendRemoteControlDelta(deviceId, delta);
    }

    async sendProgramStatusDelta(
        deviceId: string,
        partial: Partial<ProgramStatusData>,
    ): Promise<void> {
        const delta = mapProgramStatusDelta(partial);
        await this.sendRemoteControlDelta(deviceId, delta);
    }

    async sendHamlibControlDelta(
        deviceId: string,
        partial: Partial<HamlibControlData>,
    ): Promise<void> {
        const delta = mapHamlibControlDelta(partial);
        await this.sendRemoteControlDelta(deviceId, delta);
    }

    async sendProjectParametersDelta(
        deviceId: string,
        partial: Partial<AmplifierProjectData>,
    ): Promise<void> {
        const data = mapAmplifierProjectDelta(partial);
        if (!data.calibration && !data.protection && !data.band) return;
        try {
            await this.publishJsonCommand(deviceId, {
                msg_id: createRemoteControlMessageId(),
                cmd: 'eng_params',
                timestamp: Math.floor(Date.now() / 1000),
                data,
            });
        } catch (error) {
            console.error('AmplifierMqttService: eng_params 写入失败:', error);
            throw error;
        }
    }

    /** 协议 §5.9.4：空 data 查询完整工程参数 */
    async queryEngParams(deviceId: string): Promise<void> {
        try {
            await this.publishJsonCommand(deviceId, {
                msg_id: createRemoteControlMessageId(),
                cmd: 'eng_params',
                timestamp: Math.floor(Date.now() / 1000),
                data: {},
            });
        } catch (error) {
            console.error('AmplifierMqttService: eng_params 查询失败:', error);
            throw error;
        }
    }

    /** 协议 §5.10：工程参数备份 list / create / restore / delete / purge */
    async sendEngParamsBackup(
        deviceId: string,
        data: Pick<EngParamsBackupData, 'action' | 'id' | 'checksum'>,
    ): Promise<void> {
        const action = data.action as EngParamsBackupAction | undefined;
        if (!action) {
            throw new Error('eng_params_backup 缺少 action');
        }
        const payload: EngParamsBackupData = { action };
        if (action === 'restore' || action === 'delete') {
            if (!data.id) {
                throw new Error(`eng_params_backup ${action} 缺少 id`);
            }
            payload.id = data.id;
        }
        if (action === 'restore') {
            if (!data.checksum) {
                throw new Error('eng_params_backup restore 缺少 checksum');
            }
            payload.checksum = data.checksum;
        }
        try {
            await this.publishJsonCommand(deviceId, {
                msg_id: createRemoteControlMessageId(),
                cmd: 'eng_params_backup',
                timestamp: Math.floor(Date.now() / 1000),
                data: payload,
            });
        } catch (error) {
            console.error('AmplifierMqttService: eng_params_backup 发送失败:', error);
            throw error;
        }
    }

    addBackupListener(
        deviceId: string,
        callback: (message: EngParamsBackupMessage) => void,
    ): () => void {
        const listeners = this.backupListenersByDevice.get(deviceId) ?? new Set();
        listeners.add(callback);
        this.backupListenersByDevice.set(deviceId, listeners);
        return () => {
            const current = this.backupListenersByDevice.get(deviceId);
            if (!current) return;
            current.delete(callback);
            if (current.size === 0) {
                this.backupListenersByDevice.delete(deviceId);
            }
        };
    }

    /** @deprecated 请使用 sendAmplifierControlDelta，仅传变更字段 */
    async sendAmpConfigCommand(deviceId: string, controlData: Partial<AmplifierControlData>): Promise<void> {
        try {
            await this.sendAmplifierControlDelta(deviceId, controlData);
        } catch (error) {
            console.error('AmplifierMqttService: 功放控制指令发送失败:', error);
            throw error;
        }
    }

    /** @deprecated 请使用 sendProjectParametersDelta，仅传变更字段 */
    async sendProjectParametersCommand(deviceId: string, projectData: Partial<AmplifierProjectData>): Promise<void> {
        try {
            await this.sendProjectParametersDelta(deviceId, projectData);
        } catch (error) {
            console.error('AmplifierMqttService: 工程参数指令发送失败:', error);
            throw error;
        }
    }


    /**
     * 停止参数命令定时任务
     */
    stopParamCommandInterval(): void {
        if (this.paramCommandIntervalId) {
            clearInterval(this.paramCommandIntervalId);
            this.paramCommandIntervalId = null;
            console.log('AmplifierMqttService: 参数命令定时任务已停止');
        }
    }


    /** 状态回调节流间隔（ms），降低 setState 频率以提升渲染效率，建议 80～150 */
    private static readonly STATUS_THROTTLE_MS = 100;
    /** 设备 -> 多订阅者（卡片/详情并存） */
    private subscribersByDevice = new Map<string, Map<string, AmplifierStatusSubscriber>>();
    /** 设备 -> 待下发消息（按设备隔离节流） */
    private pendingByDevice = new Map<string, PendingByDevice>();
    private lastAmplifierByDevice = new Map<string, AmplifierData>();
    private lastProjectDataByDevice = new Map<string, AmplifierProjectData>();
    /** addMessageListener 取消函数；勿用 setCallbacks.onMessage，会覆盖 MqttAudioManager */
    private messageListenerUnsub: (() => void) | null = null;
    private subscriberCounter = 0;
    /** 工程参数备份应答监听（协议 §5.10） */
    private backupListenersByDevice = new Map<string, Set<(message: EngParamsBackupMessage) => void>>();

    private getDevicePending(deviceId: string): PendingByDevice {
        const current = this.pendingByDevice.get(deviceId);
        if (current) return current;
        const created: PendingByDevice = {
            pendingAmplifierData: null,
            pendingProgramStatus: null,
            pendingHamlib: null,
            pendingAuditLog: null,
            pendingProjectData: null,
            throttleTimerId: null,
        };
        this.pendingByDevice.set(deviceId, created);
        return created;
    }

    private flushDevice(deviceId: string): void {
        const pending = this.pendingByDevice.get(deviceId);
        if (!pending) return;
        pending.throttleTimerId = null;
        const subscribers = this.subscribersByDevice.get(deviceId);
        if (!subscribers || subscribers.size === 0) {
            this.clearThrottle(deviceId);
            return;
        }

        const amplifierData = pending.pendingAmplifierData;
        const programStatus = pending.pendingProgramStatus;
        const hamlib = pending.pendingHamlib;
        const auditLog = pending.pendingAuditLog;
        const projectData = pending.pendingProjectData;
        pending.pendingAmplifierData = null;
        pending.pendingProgramStatus = null;
        pending.pendingHamlib = null;
        pending.pendingAuditLog = null;
        pending.pendingProjectData = null;

        subscribers.forEach((subscriber) => {
            try {
                if (amplifierData !== null) {
                    subscriber.callback(amplifierData);
                }
                if (programStatus !== null && subscriber.programStatusCallback) {
                    subscriber.programStatusCallback(programStatus);
                }
                if (hamlib !== null && subscriber.hamlibControlCallback) {
                    subscriber.hamlibControlCallback(hamlib);
                }
                if (auditLog !== null && subscriber.auditLogCallback) {
                    subscriber.auditLogCallback(auditLog);
                }
                if (projectData !== null && subscriber.projectDataCallback) {
                    subscriber.projectDataCallback(projectData);
                }
            } catch (error) {
                console.error('AmplifierMqttService: 分发设备状态回调失败:', error);
            }
        });
    }

    private scheduleFlush(deviceId: string): void {
        const pending = this.getDevicePending(deviceId);
        if (pending.throttleTimerId !== null) return;
        pending.throttleTimerId = setTimeout(() => {
            this.flushDevice(deviceId);
        }, AmplifierMqttService.STATUS_THROTTLE_MS);
    }

    /** 协议 §5.7：remote_popup 为瞬时 UI 态，即时分发，不走状态节流 */
    private dispatchRemotePopup(deviceId: string, state: RemotePopupState): void {
        const subscribers = this.subscribersByDevice.get(deviceId);
        if (!subscribers || subscribers.size === 0) {
            return;
        }
        subscribers.forEach((subscriber) => {
            try {
                subscriber.remotePopupCallback?.(state);
            } catch (error) {
                console.error('AmplifierMqttService: 分发 remote_popup 回调失败:', error);
            }
        });
    }

    private handleRemotePopupMessage(deviceId: string, jsonMessage: RemotePopupMessage): void {
        const payload = jsonMessage.data;
        if (!payload || typeof payload.open !== 'boolean') {
            if (process.env.NODE_ENV === 'development') {
                console.warn('AmplifierMqttService: 忽略无效 remote_popup 上行:', jsonMessage);
            }
            return;
        }
        if (payload.action !== undefined) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('AmplifierMqttService: 忽略带 action 的 remote_popup 上行:', jsonMessage);
            }
            return;
        }

        const popupType = defaultRemotePopupType(payload.popup_type);
        const popupId =
            typeof payload.popup_id === 'string' && payload.popup_id.trim()
                ? payload.popup_id.trim()
                : payload.open && jsonMessage.msg_id
                  ? String(jsonMessage.msg_id)
                  : undefined;
        const state: RemotePopupState = {
            open: payload.open,
            message: payload.open ? String(payload.message ?? '') : '',
            popupId,
            popupType: payload.open ? popupType : undefined,
        };
        this.dispatchRemotePopup(deviceId, state);
    }

    private handleStatusTopic(deviceId: string, message: any): void {
        try {
            const messageString = typeof message === 'string' ? message : message.toString();
            const data = { ...(JSON.parse(messageString) as AmplifierData), _source: 'legacy_status' as const };
            const pending = this.getDevicePending(deviceId);
            pending.pendingAmplifierData = data;
            this.lastAmplifierByDevice.set(deviceId, data);
            this.scheduleFlush(deviceId);
        } catch (error) {
            console.error('AmplifierMqttService: 解析设备状态消息失败:', error);
        }
    }

    private normalizeDeviceId(value: unknown): string | null {
        if (typeof value === 'number' && Number.isFinite(value)) return String(value);
        if (typeof value === 'string' && value.trim() !== '') {
            const trimmed = value.trim();
            const parsed = Number(trimmed);
            return Number.isFinite(parsed) ? String(parsed) : trimmed;
        }
        return null;
    }

    private getRemoteControlDeviceIdForTopic(deviceId: string): string | null {
        const subscribers = this.subscribersByDevice.get(deviceId);
        if (!subscribers) return null;
        let expectedDeviceId: string | null = null;
        subscribers.forEach((subscriber) => {
            if (expectedDeviceId) return;
            const normalized = this.normalizeDeviceId(subscriber.remoteControlDeviceId);
            if (normalized) expectedDeviceId = normalized;
        });
        return expectedDeviceId;
    }

    private isRemoteControlForSubscribedDevice(deviceId: string, msg: RemoteControlMessage): boolean {
        const payloadDeviceId = this.normalizeDeviceId(msg.data?.device_id);
        if (!payloadDeviceId) return true;
        const expectedDeviceId = this.getRemoteControlDeviceIdForTopic(deviceId);
        if (!expectedDeviceId) return true;
        return payloadDeviceId === expectedDeviceId;
    }

    private handleRemoteControlMessage(deviceId: string, jsonMessage: RemoteControlMessage): void {
        if (isRemoteControlError(jsonMessage.data)) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('AmplifierMqttService: 忽略 remote_control 错误包:', jsonMessage.data);
            }
            return;
        }

        if (!this.isRemoteControlForSubscribedDevice(deviceId, jsonMessage)) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('AmplifierMqttService: 忽略 device_id 不匹配的 remote_control:', {
                    topicDeviceId: deviceId,
                    expectedRemoteControlDeviceId: this.getRemoteControlDeviceIdForTopic(deviceId),
                    payloadDeviceId: jsonMessage.data?.device_id,
                });
            }
            return;
        }

        const pending = this.getDevicePending(deviceId);
        const amplifierData = mapRemoteControlToAmplifierData(
            jsonMessage,
            this.lastAmplifierByDevice.get(deviceId),
        );
        pending.pendingAmplifierData = amplifierData;
        pending.pendingProgramStatus = mapRemoteControlToProgramStatus(jsonMessage);
        pending.pendingHamlib = mapRemoteControlToHamlibControl(jsonMessage);
        this.lastAmplifierByDevice.set(deviceId, amplifierData);

        const projectData = mapEngParamsToProjectData(
            jsonMessage.data?.eng_params,
            this.lastProjectDataByDevice.get(deviceId),
        );
        if (projectData) {
            pending.pendingProjectData = projectData;
            this.lastProjectDataByDevice.set(deviceId, projectData);
        }

        this.scheduleFlush(deviceId);
    }

    private handleEngParamsMessage(deviceId: string, jsonMessage: EngParamsMessage): void {
        const data = jsonMessage.data ?? {};
        if (isRemoteControlError(data)) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('AmplifierMqttService: 忽略 eng_params 错误包:', data);
            }
            return;
        }

        const payloadDeviceId = this.normalizeDeviceId(data.device_id);
        const topicDeviceId = this.normalizeDeviceId(deviceId);
        const remoteControlDeviceId = this.getRemoteControlDeviceIdForTopic(deviceId);
        const matchesTopic = Boolean(payloadDeviceId && topicDeviceId && payloadDeviceId === topicDeviceId);
        const matchesRemoteControl = Boolean(
            payloadDeviceId && remoteControlDeviceId && payloadDeviceId === remoteControlDeviceId,
        );
        if (payloadDeviceId && !matchesTopic && !matchesRemoteControl) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('AmplifierMqttService: 忽略 device_id 不匹配的 eng_params:', {
                    topicDeviceId,
                    expectedRemoteControlDeviceId: remoteControlDeviceId,
                    payloadDeviceId,
                });
            }
            return;
        }

        const projectData = mapEngParamsToProjectData(
            data,
            this.lastProjectDataByDevice.get(deviceId),
        );
        if (!projectData) return;

        const pending = this.getDevicePending(deviceId);
        pending.pendingProjectData = projectData;
        this.lastProjectDataByDevice.set(deviceId, projectData);
        this.scheduleFlush(deviceId);
    }

    private isEngParamsPayloadForDevice(deviceId: string, payloadDeviceId: unknown): boolean {
        const normalizedPayload = this.normalizeDeviceId(payloadDeviceId);
        if (!normalizedPayload) return true;
        const topicDeviceId = this.normalizeDeviceId(deviceId);
        const remoteControlDeviceId = this.getRemoteControlDeviceIdForTopic(deviceId);
        const matchesTopic = Boolean(topicDeviceId && normalizedPayload === topicDeviceId);
        const matchesRemoteControl = Boolean(
            remoteControlDeviceId && normalizedPayload === remoteControlDeviceId,
        );
        return matchesTopic || matchesRemoteControl;
    }

    private handleEngParamsBackupMessage(deviceId: string, jsonMessage: EngParamsBackupMessage): void {
        const data = jsonMessage.data ?? {};
        if (!this.isEngParamsPayloadForDevice(deviceId, data.device_id)) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('AmplifierMqttService: 忽略 device_id 不匹配的 eng_params_backup:', {
                    topicDeviceId: deviceId,
                    expectedRemoteControlDeviceId: this.getRemoteControlDeviceIdForTopic(deviceId),
                    payloadDeviceId: data.device_id,
                });
            }
            return;
        }

        const listeners = this.backupListenersByDevice.get(deviceId);
        if (!listeners || listeners.size === 0) return;
        listeners.forEach((listener) => {
            try {
                listener(jsonMessage);
            } catch (error) {
                console.error('AmplifierMqttService: 分发 eng_params_backup 回调失败:', error);
            }
        });
    }

    private handleJsonStatusTopic(deviceId: string, message: any): void {
        try {
            const messageString = typeof message === 'string' ? message : message.toString();
            const jsonMessage = JSON.parse(messageString);
            const pending = this.getDevicePending(deviceId);
            if (jsonMessage.cmd === 'remote_control') {
                this.handleRemoteControlMessage(deviceId, jsonMessage as RemoteControlMessage);
            } else if (jsonMessage.cmd === 'update_program_status') {
                pending.pendingProgramStatus = mergeProgramStatusMessage(
                    pending.pendingProgramStatus,
                    jsonMessage as ProgramStatusMessage,
                );
                this.scheduleFlush(deviceId);
            } else if (jsonMessage.cmd === 'set_hamlib') {
                pending.pendingHamlib = jsonMessage as HamlibControlRequestMessage;
                this.scheduleFlush(deviceId);
            } else if (
                jsonMessage.cmd === 'update_audit_log' ||
                jsonMessage.cmd === 'update_alarm_log' ||
                jsonMessage.cmd === 'update_mcu_log'
            ) {
                pending.pendingAuditLog = jsonMessage as UpdateAlarmLogStatusMessage;
                this.scheduleFlush(deviceId);
            } else if (jsonMessage.cmd === 'eng_params') {
                this.handleEngParamsMessage(deviceId, jsonMessage as EngParamsMessage);
            } else if (jsonMessage.cmd === 'eng_params_backup') {
                this.handleEngParamsBackupMessage(deviceId, jsonMessage as EngParamsBackupMessage);
            } else if (jsonMessage.cmd === 'remote_popup') {
                this.handleRemotePopupMessage(deviceId, jsonMessage as RemotePopupMessage);
            }
        } catch (error) {
            console.error('AmplifierMqttService: 解析JSON状态消息失败:', error);
        }
    }

    private ensureMessageCallbacksInstalled(): void {
        if (this.messageListenerUnsub) {
            return;
        }
        if (!mqttService.isServiceInitialized()) {
            return;
        }
        const mqttManager = mqttService.getMqttManager();
        const isDev = process.env.NODE_ENV === 'development';
        this.messageListenerUnsub = mqttManager.addMessageListener(
            (receivedTopic: string, message: string | Buffer) => {
                const serverMatch = receivedTopic.match(/^server\/device\/([^/]+)\/(status|json_status)$/);
                const amplifierMatch = receivedTopic.match(/^amplifier\/device\/([^/]+)\/json_status$/);
                const deviceId = serverMatch?.[1] ?? amplifierMatch?.[1];
                const suffix = serverMatch?.[2] ?? (amplifierMatch ? 'json_status' : undefined);
                if (!deviceId || !suffix) {
                    return;
                }
                const subscribers = this.subscribersByDevice.get(deviceId);
                if (!subscribers || subscribers.size === 0) {
                    return;
                }
                if (suffix === 'status') {
                    this.handleStatusTopic(deviceId, message);
                } else {
                    this.handleJsonStatusTopic(deviceId, message);
                }
                if (isDev) {
                    console.log(
                        `AmplifierMqttService: 已分发 ${receivedTopic} 给 ${subscribers.size} 个订阅者`,
                    );
                }
            },
        );
    }

    /**
     * 订阅设备状态主题
     * 订阅 server/device/{deviceId}/status 主题并处理接收到的消息
     * 接收效率：热路径仅做一次解析、按 topic 分支；生产环境减少 log。
     * 渲染效率：对回调做节流，合并短时间内的多次消息为一次下发，减少 React 重绘。
     */
    async subscribeToDeviceStatus(
        deviceId: string,
        callback: (data: AmplifierData) => void,
        programStatusCallback?: (data: ProgramStatusMessage) => void,
        hamlibControlCallback?: (data: HamlibControlRequestMessage) => void,
        auditLogCallback?: (data: UpdateAlarmLogStatusMessage) => void,
        projectDataCallback?: (data: AmplifierProjectData) => void,
        remotePopupCallback?: (state: RemotePopupState) => void,
        subscriberKey?: string,
        remoteControlDeviceId?: string | number | null
    ): Promise<void> {
        const key = subscriberKey ?? `subscriber-${++this.subscriberCounter}`;
        try {
            if (!mqttService.isConnected()) {
                try {
                    await mqttService.connect();
                } catch (connectError) {
                    if (this.isDevMqttOptional()) {
                        return;
                    }
                    console.error('AmplifierMqttService: 连接MQTT服务失败:', connectError);
                    throw connectError;
                }
            }

            if (!mqttService.isServiceInitialized()) {
                if (this.isDevMqttOptional()) {
                    return;
                }
                console.error('AmplifierMqttService: MQTT服务未初始化，无法订阅设备状态主题');
                throw new Error('MQTT服务未初始化');
            }

            const mqttManager = mqttService.getMqttManager();
            const topic = `server/device/${deviceId}/status`;
            const jsonStatusTopic = `server/device/${deviceId}/json_status`;
            const amplifierJsonStatusTopic = `amplifier/device/${deviceId}/json_status`;
            const isDev = process.env.NODE_ENV === 'development';
            const subscribers = this.subscribersByDevice.get(deviceId) ?? new Map<string, AmplifierStatusSubscriber>();
            const hadSubscribers = subscribers.size > 0;
            subscribers.set(key, {
                callback,
                programStatusCallback,
                hamlibControlCallback,
                auditLogCallback,
                projectDataCallback,
                remotePopupCallback,
                remoteControlDeviceId: this.normalizeDeviceId(remoteControlDeviceId) ?? undefined,
            });
            this.subscribersByDevice.set(deviceId, subscribers);

            if (!hadSubscribers) {
                await mqttManager.subscribe(topic, 1);
                await mqttManager.subscribe(jsonStatusTopic, 1);
                await mqttManager.subscribe(amplifierJsonStatusTopic, 1);
                if (isDev) {
                    console.log(
                        'AmplifierMqttService: 已订阅设备状态主题:',
                        topic,
                        jsonStatusTopic,
                        amplifierJsonStatusTopic,
                    );
                }
            }
            this.ensureMessageCallbacksInstalled();
        } catch (error) {
            if (this.isDevMqttOptional()) {
                const subscribers = this.subscribersByDevice.get(deviceId);
                if (subscribers) {
                    subscribers.delete(key);
                    if (subscribers.size === 0) {
                        this.subscribersByDevice.delete(deviceId);
                    }
                }
                return;
            }
            console.error('AmplifierMqttService: 订阅设备状态主题失败:', error);
            const subscribers = this.subscribersByDevice.get(deviceId);
            if (subscribers) {
                subscribers.delete(key);
                if (subscribers.size === 0) {
                    this.subscribersByDevice.delete(deviceId);
                }
            }
            throw error;
        }
    }

    /** 清除节流定时器与待下发数据，避免卸载后仍触发回调 */
    private clearThrottle(deviceId?: string): void {
        if (deviceId) {
            const pending = this.pendingByDevice.get(deviceId);
            if (!pending) return;
            if (pending.throttleTimerId !== null) {
                clearTimeout(pending.throttleTimerId);
            }
            this.pendingByDevice.delete(deviceId);
            this.lastAmplifierByDevice.delete(deviceId);
            this.lastProjectDataByDevice.delete(deviceId);
            return;
        }
        this.pendingByDevice.forEach((pending) => {
            if (pending.throttleTimerId !== null) {
                clearTimeout(pending.throttleTimerId);
            }
        });
        this.pendingByDevice.clear();
        this.lastAmplifierByDevice.clear();
        this.lastProjectDataByDevice.clear();
    }

    /**
     * 取消订阅设备状态主题
     */
    async unsubscribeFromDeviceStatus(deviceId: string, subscriberKey?: string): Promise<void> {
        try {
            const subscribers = this.subscribersByDevice.get(deviceId);
            if (!subscribers || subscribers.size === 0) {
                this.clearThrottle(deviceId);
                return;
            }

            if (subscriberKey) {
                subscribers.delete(subscriberKey);
            } else {
                subscribers.clear();
            }
            if (subscribers.size > 0) return;

            this.subscribersByDevice.delete(deviceId);
            this.clearThrottle(deviceId);

            if (!mqttService.isConnected()) return;
            if (!mqttService.isServiceInitialized()) return;
            const mqttManager = mqttService.getMqttManager();
            const topic = `server/device/${deviceId}/status`;
            const jsonStatusTopic = `server/device/${deviceId}/json_status`;
            const amplifierJsonStatusTopic = `amplifier/device/${deviceId}/json_status`;
            await mqttManager.unsubscribe(topic);
            await mqttManager.unsubscribe(jsonStatusTopic);
            await mqttManager.unsubscribe(amplifierJsonStatusTopic);
            console.log(
                'AmplifierMqttService: 已取消订阅设备状态主题:',
                topic,
                jsonStatusTopic,
                amplifierJsonStatusTopic,
            );
        } catch (error) {
            console.error('AmplifierMqttService: 取消订阅设备状态主题失败:', error);
            // 不抛出错误，避免影响组件渲染
        }
    }

    /**
     * 销毁服务
     */
    dispose(): void {
        this.stopParamCommandInterval();
        this.clearThrottle();
        this.subscribersByDevice.clear();
        this.backupListenersByDevice.clear();
        this.messageListenerUnsub?.();
        this.messageListenerUnsub = null;
    }
}

// 创建服务实例
const amplifierMqttService = new AmplifierMqttService();

// 导出服务实例
export { amplifierMqttService };
